import type { PublicSiteLocale, PublicSiteTypographyPolicy } from './publicSiteTypes'

export const PUBLIC_SITE_MAJOR_LOCALES: PublicSiteLocale[] = ['en', 'zh-Hans', 'es', 'hi', 'fr', 'ar', 'pt']

export const PUBLIC_SITE_LOCALE_STORAGE_KEY = 'timelining.public.locale'

export const PUBLIC_SITE_LOCALE_LABELS: Record<PublicSiteLocale, { label: string; nativeLabel: string }> = {
  en: { label: 'English', nativeLabel: 'English' },
  'zh-Hans': { label: 'Mandarin Chinese', nativeLabel: '简体中文' },
  es: { label: 'Spanish', nativeLabel: 'Español' },
  hi: { label: 'Hindi', nativeLabel: 'हिन्दी' },
  fr: { label: 'French', nativeLabel: 'Français' },
  ar: { label: 'Standard Arabic', nativeLabel: 'العربية' },
  pt: { label: 'Portuguese', nativeLabel: 'Português' },
}

export const PUBLIC_SITE_TYPOGRAPHY: Record<PublicSiteLocale, PublicSiteTypographyPolicy> = {
  en: { dir: 'ltr', fontScale: 1, compactFontScale: 1, lineHeight: 1.5, compactLineHeight: 1.2 },
  'zh-Hans': { dir: 'ltr', fontScale: 0.98, compactFontScale: 0.94, lineHeight: 1.58, compactLineHeight: 1.28 },
  es: { dir: 'ltr', fontScale: 0.99, compactFontScale: 0.96, lineHeight: 1.52, compactLineHeight: 1.22 },
  hi: { dir: 'ltr', fontScale: 0.98, compactFontScale: 0.95, lineHeight: 1.68, compactLineHeight: 1.36 },
  fr: { dir: 'ltr', fontScale: 0.99, compactFontScale: 0.96, lineHeight: 1.52, compactLineHeight: 1.22 },
  ar: { dir: 'rtl', fontScale: 1, compactFontScale: 0.97, lineHeight: 1.68, compactLineHeight: 1.36 },
  pt: { dir: 'ltr', fontScale: 0.99, compactFontScale: 0.96, lineHeight: 1.52, compactLineHeight: 1.22 },
}

export const normalizePublicSiteLocale = (locale: string | null | undefined): PublicSiteLocale => {
  if (!locale) return 'en'
  const normalized = locale.trim()
  if (PUBLIC_SITE_MAJOR_LOCALES.includes(normalized as PublicSiteLocale)) {
    return normalized as PublicSiteLocale
  }
  const lower = normalized.toLowerCase()
  if (
    lower === 'zh'
    || lower === 'zh-cn'
    || lower === 'zh-sg'
    || lower === 'zh-tw'
    || lower === 'zh-hk'
    || lower === 'zh-mo'
    || lower.startsWith('zh-hans')
    || lower.startsWith('zh-hant')
  ) return 'zh-Hans'
  if (lower.startsWith('es')) return 'es'
  if (lower.startsWith('hi')) return 'hi'
  if (lower.startsWith('fr')) return 'fr'
  if (lower.startsWith('ar')) return 'ar'
  if (lower.startsWith('pt')) return 'pt'
  return 'en'
}

export const getStoredPublicSiteLocale = (): PublicSiteLocale => {
  if (typeof window === 'undefined') return 'en'
  try {
    const stored = window.localStorage.getItem(PUBLIC_SITE_LOCALE_STORAGE_KEY)
    if (stored) return normalizePublicSiteLocale(stored)
  } catch {
    // localStorage can be disabled; fall back to browser language below.
  }
  const browserLanguage = window.navigator?.language ?? window.navigator?.languages?.[0]
  return normalizePublicSiteLocale(browserLanguage)
}

export const setStoredPublicSiteLocale = (locale: PublicSiteLocale): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PUBLIC_SITE_LOCALE_STORAGE_KEY, locale)
  } catch {
    // ignore storage quota/privacy failures; runtime state still changes.
  }
}
